import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { supabaseAdmin, hasServiceRoleKey } from "@/integrations/supabase/admin-client";

export interface Category {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  image: string | null;
  banner_image: string | null;
  product_count: number;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface CreateCategoryData {
  name: string;
  slug: string;
  description?: string;
  image?: string;
  banner_image?: string;
  is_active?: boolean;
  sort_order?: number;
}

export interface UpdateCategoryData extends Partial<CreateCategoryData> {
  id: string;
}

export function useCategories() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from("categories")
        .select("*")
        .order("sort_order", { ascending: true })
        .order("name", { ascending: true });

      if (error) throw error;
      setCategories(data || []);
    } catch (err) {
      console.error("Error fetching categories:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch categories");
    } finally {
      setLoading(false);
    }
  };

  const createCategory = async (categoryData: CreateCategoryData): Promise<Category | null> => {
    try {
      setError(null);
      
      const { data, error } = await supabase
        .from("categories")
        .insert([{
          ...categoryData,
          product_count: 0,
          is_active: categoryData.is_active ?? true,
          sort_order: categoryData.sort_order ?? 0,
        }])
        .select()
        .single();

      if (error) throw error;
      
      // Update local state
      setCategories(prev => [...prev, data].sort((a, b) => a.sort_order - b.sort_order));
      return data;
    } catch (err) {
      console.error("Error creating category:", err);
      setError(err instanceof Error ? err.message : "Failed to create category");
      return null;
    }
  };

  const updateCategory = async (categoryData: UpdateCategoryData): Promise<Category | null> => {
    try {
      setError(null);
      
      const { id, ...updateData } = categoryData;
      
      const { data, error } = await supabase
        .from("categories")
        .update(updateData)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      
      // Update local state
      setCategories(prev => prev.map(c => c.id === id ? data : c));
      return data;
    } catch (err) {
      console.error("Error updating category:", err);
      setError(err instanceof Error ? err.message : "Failed to update category");
      return null;
    }
  };

  const deleteCategory = async (categoryId: string): Promise<boolean> => {
    try {
      setError(null);
      
      const { error } = await supabase
        .from("categories")
        .delete()
        .eq("id", categoryId);

      if (error) throw error;
      
      // Update local state
      setCategories(prev => prev.filter(c => c.id !== categoryId));
      return true;
    } catch (err) {
      console.error("Error deleting category:", err);
      setError(err instanceof Error ? err.message : "Failed to delete category");
      return false;
    }
  };

  const getActiveCategories = (): Category[] => {
    return categories.filter(category => category.is_active);
  };

  const getCategoryById = async (categoryId: string): Promise<Category | null> => {
    try {
      setError(null);
      
      const { data, error } = await supabase
        .from("categories")
        .select("*")
        .eq("id", categoryId)
        .single();

      if (error) throw error;
      return data;
    } catch (err) {
      console.error("Error fetching category:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch category");
      return null;
    }
  };

  const getCategoryBySlug = async (slug: string): Promise<Category | null> => {
    try {
      setError(null);
      
      const { data, error } = await supabase
        .from("categories")
        .select("*")
        .eq("slug", slug)
        .single();

      if (error) throw error;
      return data;
    } catch (err) {
      console.error("Error fetching category by slug:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch category by slug");
      return null;
    }
  };

  const reorderCategories = async (reorderedCategories: Category[]): Promise<boolean> => {
    try {
      setError(null);
      
      console.log('Starting category reorder with:', reorderedCategories.length, 'categories');
      
      // Update sort_order for each category based on its new position
      const updates = reorderedCategories.map((category, index) => ({
        id: category.id,
        sort_order: index + 1
      }));

      console.log('Updates to perform:', updates);

      // Use admin client if we have service role key, otherwise use regular client
      const client = hasServiceRoleKey() ? supabaseAdmin : supabase;
      console.log('Using client:', hasServiceRoleKey() ? 'admin (service role)' : 'regular (anon key)');
      
      // Perform individual updates for each category
      const updatePromises = updates.map(({ id, sort_order }) => {
        console.log(`Updating category ${id} to sort_order ${sort_order}`);
        return client
          .from("categories")
          .update({ sort_order })
          .eq("id", id);
      });

      const results = await Promise.all(updatePromises);
      
      console.log('Update results:', results);
      
      // Check if any updates failed
      const hasError = results.some(result => result.error);
      if (hasError) {
        const errors = results.filter(result => result.error).map(result => result.error);
        console.error('Update errors:', errors);
        throw new Error(`Update failed: ${errors.map(e => e.message).join(', ')}`);
      }
      
      // Update local state with new sort orders
      setCategories(prev => {
        const updated = prev.map(category => {
          const update = updates.find(u => u.id === category.id);
          return update ? { ...category, sort_order: update.sort_order } : category;
        });
        return updated.sort((a, b) => a.sort_order - b.sort_order);
      });
      
      console.log('Category reorder completed successfully');
      return true;
    } catch (err) {
      console.error("Error reordering categories:", err);
      setError(err instanceof Error ? err.message : "Failed to reorder categories");
      return false;
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  return {
    categories,
    loading,
    error,
    fetchCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    getActiveCategories,
    getCategoryById,
    getCategoryBySlug,
    reorderCategories,
  };
} 